package com.siriusxm.pia.operations.components

import androidx.compose.runtime.Composable
import com.siriusxm.unifiedcontent.cmc.MonitorState
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

/**
 * A small colored circle indicating the status of a DataDog monitor.
 *
 * Colors match DataDog's monitor status indicators:
 * - OK: Green (#4caf50)
 * - Alert: Red (#f44336)
 * - Warn: Orange (#ff9800)
 * - No Data/Other: Gray (#9e9e9e)
 *
 * @param status The monitor state to display
 * @param size Optional size override (default 10px)
 */
@Composable
fun MonitorStatusIndicator(status: MonitorState?, size: CSSNumeric = 10.px) {
    val statusStyle = when (status) {
        MonitorState.OK -> OperationsStyles.monitorStatusOk
        MonitorState.ALERT -> OperationsStyles.monitorStatusAlert
        MonitorState.WARN -> OperationsStyles.monitorStatusWarn
        MonitorState.NO_DATA -> OperationsStyles.monitorStatusNoData
        MonitorState.IGNORED -> OperationsStyles.monitorStatusNoData
        MonitorState.SKIPPED -> OperationsStyles.monitorStatusNoData
        else -> OperationsStyles.monitorStatusNoData
    }

    Div({
        classes(OperationsStyles.monitorStatusIndicator, statusStyle)
        style {
            width(size)
            height(size)
        }
        attr("title", status.toLabel())
    })
}

/**
 * Returns a human-readable label for a monitor state.
 */
fun MonitorState?.toLabel(): String {
    return when (this) {
        MonitorState.OK -> "OK"
        MonitorState.ALERT -> "Alert"
        MonitorState.WARN -> "Warning"
        MonitorState.NO_DATA -> "No Data"
        MonitorState.IGNORED -> "Ignored"
        MonitorState.SKIPPED -> "Skipped"
        // smithy4kt names the catch-all variant `_Unknown` (renamed from `Unknown` in 1.1.30); it
        // carries whatever status string DataDog sent that the model does not enumerate.
        is MonitorState._Unknown -> this.name
        null -> "Unknown"
    }
}

/**
 * Returns true if the monitor is in an alerting state (Alert or Warn).
 */
fun MonitorState?.isAlerting(): Boolean {
    return this == MonitorState.ALERT || this == MonitorState.WARN
}

/**
 * Returns true if the monitor is healthy (OK).
 */
fun MonitorState?.isHealthy(): Boolean {
    return this == MonitorState.OK
}
